import * as React from 'react';

import {
  TABLE_BODY_CELL,
  TABLE_FRAME,
  TABLE_HEADER_SURFACE,
  TABLE_HEAD_CELL,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
  /**
   * Drops the frame, for the one case that earns it: a table that IS the whole
   * content area (Linear/Notion-style), where the viewport is already the frame.
   * See `TABLE_FRAME` for the rule. Do not reach for this to make a table
   * "quieter" — quiet is what the hairlines are for.
   */
  unframed?: boolean;
  /** Rendered inside the frame, below the table — the pagination footer's home. */
  footer?: React.ReactNode;
  /**
   * Pins the first column to the left edge of the table's own scroller, so a
   * wide metrics table scrolls its numbers sideways while the row's name stays
   * readable. The pinned cells carry an opaque ground (page background, with the
   * header band and the row hover painted on a layer behind the text) so
   * scrolled cells never show through them.
   */
  stickyFirstColumn?: boolean;
  /**
   * Pins the first N columns (1 or 2). The second pinned column sits at
   * `left-11`, so the first must be exactly 2.75rem wide.
   */
  stickyColumns?: 1 | 2;
}

const STICKY_BASE = [
  '[&_tr>:first-child]:sticky [&_tr>:first-child]:left-0 [&_tr>:first-child]:z-10 [&_tr>:first-child]:bg-background',
  '[&_tr>:first-child]:before:absolute [&_tr>:first-child]:before:inset-0 [&_tr>:first-child]:before:-z-10',
  '[&_thead_tr>:first-child]:before:bg-muted/20',
  '[&_tbody_tr:hover>:first-child]:before:bg-muted/40',
].join(' ');

const STICKY_SECOND = [
  '[&_tr>:nth-child(2)]:sticky [&_tr>:nth-child(2)]:left-0 sm:[&_tr>:nth-child(2)]:left-11 [&_tr>:nth-child(2)]:z-10 [&_tr>:nth-child(2)]:bg-background',
  '[&_tr>:nth-child(2)]:before:absolute [&_tr>:nth-child(2)]:before:inset-0 [&_tr>:nth-child(2)]:before:-z-10',
  '[&_thead_tr>:nth-child(2)]:before:bg-muted/20',
  '[&_tbody_tr:hover>:nth-child(2)]:before:bg-muted/40',
].join(' ');

const EDGE_FIRST =
  'group-data-[scrolled=true]/scroll:[&_tr>:first-child]:shadow-[inset_-1px_0_0_var(--color-border)]';
const EDGE_SECOND =
  'group-data-[scrolled=true]/scroll:[&_tr>:nth-child(2)]:shadow-[inset_-1px_0_0_var(--color-border)]';

function stickyClasses(columns: 0 | 1 | 2): string {
  if (columns === 0) return '';
  return columns === 1
    ? `${STICKY_BASE} ${EDGE_FIRST}`
    : `${STICKY_BASE} ${STICKY_SECOND} ${EDGE_SECOND}`;
}

/**
 * The frame is applied HERE, not at call sites, so a table cannot ship without
 * it by omission. `footer` exists for the same reason: a pagination row rendered
 * as a sibling below the table sits outside the frame and reads as loose
 * controls floating under a list (founder, 2026-08-23), which is precisely the
 * thing the frame is meant to prevent.
 */
const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    { className, containerClassName, unframed, footer, stickyFirstColumn, stickyColumns, ...props },
    ref,
  ) => {
    const pinned: 0 | 1 | 2 = stickyColumns ?? (stickyFirstColumn ? 1 : 0);
    const scrollerRef = React.useRef<HTMLDivElement>(null);
    // A table wider than its frame scrolls inside it, and a header half-cut at
    // the edge reads as a bug. While more of the table lies off to the right the
    // edge fades out, which says "this continues" without drawing a control.
    React.useEffect(() => {
      const scroller = scrollerRef.current;
      if (!scroller || !pinned) return;
      const update = () => {
        const more = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
        scroller.dataset.more = more ? 'true' : 'false';
      };
      update();
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(update);
      observer.observe(scroller);
      if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
      return () => observer.disconnect();
    }, [pinned]);
    return (
      <div className={cn('w-full', !unframed && TABLE_FRAME, containerClassName)}>
        <div
          ref={scrollerRef}
          className="group/scroll relative w-full overflow-auto data-[more=true]:[mask-image:linear-gradient(to_right,black_calc(100%_-_1.5rem),transparent)]"
          {...(pinned
            ? {
                'data-scrolled': 'false',
                onScroll: (event: React.UIEvent<HTMLDivElement>) => {
                  const scroller = event.currentTarget;
                  scroller.dataset.scrolled = scroller.scrollLeft > 0 ? 'true' : 'false';
                  scroller.dataset.more =
                    scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1
                      ? 'true'
                      : 'false';
                },
              }
            : {})}
        >
          <table
            ref={ref}
            className={cn('w-full caption-bottom text-sm', stickyClasses(pinned), className)}
            {...props}
          />
        </div>
        {footer}
      </div>
    );
  },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:border-b [&_tr]:border-border/40 [&_tr]:hover:bg-transparent',
      TABLE_HEADER_SURFACE,
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-border bg-accent/10 font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        typeof props['data-header'] === 'undefined' &&
          'border-b border-border/40 transition-colors hover:bg-muted/40 data-[state=selected]:bg-accent/30',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-left align-middle text-detail font-medium text-muted-foreground bg-transparent cursor-default select-none [&:has([role=checkbox])]:pr-0',
      TABLE_HEAD_CELL,
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('align-middle [&:has([role=checkbox])]:pr-0', TABLE_BODY_CELL, className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
